/** Общие типы для всех resource-страниц. Зеркалят формат вывода
 *  api/routes/admin.py — без внешней зависимости от OpenAPI. */

export type Tariff = "basic" | "parental" | "premium";

export const TARIFF_LABEL: Record<Tariff, string> = {
  basic: "Быстрый VPN",
  parental: "Родконтроль",
  premium: "Premium 4K",
};

export const TARIFF_COLOR: Record<Tariff, string> = {
  basic: "blue",
  parental: "purple",
  premium: "gold",
};

export interface UserListItem {
  tg_id: number;
  username: string | null;
  first_name: string | null;
  last_name: string | null;
  lang: string | null;
  flagged_abuse: boolean;
  bot_blocked: boolean;
  referred_by: number | null;
  created_at: string | null;
  last_seen_at: string | null;
  payments_total_rub: number;
  active_subs_cnt: number;
  // Анкета онбординга (миграция 011, 2026-05-14).
  // step: 0=не начата, 1=device asked, 2=os asked, 3=client asked, 4=done.
  device_type: OnboardingDevice | null;
  device_os: OnboardingOS | null;
  vpn_client: OnboardingClient | null;
  onboarding_step: number;
  onboarding_completed_at: string | null;
}

// Ключи онбординга совпадают с теми, что бот пишет в БД. См. bot/handlers/onboarding.py.
export type OnboardingDevice = "phone" | "tablet" | "laptop" | "tv" | "router";
export type OnboardingOS =
  | "ios" | "android" | "windows" | "macos" | "linux"
  | "androidtv" | "tvos" | "samsung_tizen" | "lg_webos"
  | "keenetic" | "openwrt" | "mikrotik" | "other";
export type OnboardingClient =
  | "karing" | "hiddify" | "happ" | "streisand" | "shadowrocket"
  | "v2raytun" | "v2rayng" | "nekobox" | "nekoray"
  | "v2rayn" | "v2rayu" | "v2raya" | "foxray" | "v2box"
  | "cli" | "router_ready" | "setup_help" | "other";

export const DEVICE_LABEL: Record<OnboardingDevice, string> = {
  phone:  "📱 Телефон",
  tablet: "📱 Планшет",
  laptop: "💻 Ноутбук",
  tv:     "📺 TV",
  router: "🌐 Роутер",
};

export const OS_LABEL: Record<OnboardingOS, string> = {
  ios: "iOS", android: "Android",
  windows: "Windows", macos: "macOS", linux: "Linux",
  androidtv: "Android TV", tvos: "Apple TV",
  samsung_tizen: "Samsung", lg_webos: "LG",
  keenetic: "Keenetic", openwrt: "OpenWRT", mikrotik: "Mikrotik",
  other: "Другое",
};

export const CLIENT_LABEL: Record<OnboardingClient, string> = {
  karing: "Karing", hiddify: "Hiddify", happ: "HAPP", streisand: "Streisand",
  shadowrocket: "Shadowrocket", v2raytun: "v2rayTun", v2rayng: "v2rayNG",
  nekobox: "NekoBox", nekoray: "NekoRay", v2rayn: "v2rayN",
  v2rayu: "V2rayU", v2raya: "v2rayA", foxray: "FoXray", v2box: "V2Box",
  cli: "sing-box CLI", router_ready: "Роутер уже настроен",
  setup_help: "Помогите выбрать", other: "Другое",
};

export interface SubscriptionListItem {
  id: number;
  user_id: number;
  username: string | null;
  first_name: string | null;
  tariff: Tariff;
  tariff_title: string;
  label: string | null;
  sub_token: string;
  sub_url: string;
  duration_months: number;
  start_at: string | null;
  end_at: string | null;
  active: boolean;
  is_active: boolean;
  device_limit: number;
  uuid_reset_count: number;
  hysteria_revoked_at: string | null;
  shared_block_until: string | null;
  bytes_total: number;
  online_now: number;
  // Уникальные IP за последние 30 дней — фактически использованные устройства.
  // Отличается от device_limit (разрешённый лимит, ставится админом).
  devices_used: number;
  created_at: string | null;
  is_favorite: boolean;
  // Пробный 2-дневный доступ — выдаётся через /trial в боте, один на юзера.
  // Влияет на статус в админке (показывается «🎁 Пробный» вместо «Активна»)
  // и на текст fallback-сообщения при истечении.
  is_trial: boolean;
}

export interface ConnectionLog {
  id: number;
  ip: string;
  asn: number | null;
  country: string | null;
  city: string | null;
  connected_at: string | null;
}

export interface DailyTraffic {
  day: string;
  tx_bytes: number;
  rx_bytes: number;
  online_minutes: number;
}

export interface LiveStats {
  tx_total: number;
  rx_total: number;
  online_now: number;
  last_sample_at: string | null;
}

export interface SubscriptionDetail {
  id: number;
  user_id: number;
  username: string | null;
  first_name: string | null;
  last_name: string | null;
  tariff: Tariff;
  label: string | null;
  sub_token: string;
  sub_url: string;
  duration_months: number;
  start_at: string | null;
  end_at: string | null;
  is_active: boolean;
  device_limit: number;
  shared_block_until: string | null;
  sharing_alerts_count: number;
  last_uuid_reset_at: string | null;
  uuid_reset_count: number;
  hysteria_revoked_at: string | null;
  payment_id: number | null;
  is_favorite: boolean;
  nodes: { id: number; name: string; ip: string; location: string }[];
  connection_logs: ConnectionLog[];
  daily_traffic: DailyTraffic[];
  live: LiveStats;
}

export interface PaymentListItem {
  id: number;
  user_id: number;
  username: string | null;
  first_name: string | null;
  gateway: "cryptobot" | "cryptocloud";
  ext_invoice_id: string;
  base_rub: number;
  final_rub: number;
  discount_pct: number | null;
  amount_usdt: number | null;
  status: "pending" | "paid" | "expired" | "refunded";
  created_at: string | null;
  paid_at: string | null;
  cart: { tariff: Tariff; months: number; children: number; price_rub: number }[];
}

export interface NodeListItem {
  id: number;
  name: string;
  public_ip: string;
  sub_dns: string;
  location: string;
  active: boolean;
  added_at: string | null;
  serves_basic: boolean;
  serves_parental: boolean;
  serves_premium: boolean;
  capacity_basic: number;
  capacity_parental: number;
  capacity_premium: number;
  traffic_stats_port: number | null;
  // unified-node architecture (2026-05-13)
  node_class: "standard" | "premium" | string;
  legacy_imported: boolean;
  health_status: "healthy" | "degraded" | "down" | "unknown" | string;
  health_checked_at: string | null;
  // приоритет нод (меньшее = выше). 10=primary, 100=default, 900=legacy/резерв.
  // sing-box urltest пробует outbounds в порядке priority ASC.
  priority: number;
  // active_subs/online_now/last_sample_at для legacy подмешиваются из
  // last_clients_total/last_clients_online/last_stats_at (заполняет
  // host-side cron). Для production-нод — из subscription_traffic.
  active_subs: number;
  online_now: number;
  last_sample_at: string | null;
}

// «Online сейчас» — одна строка на подписку с auth-callback за 5 мин.
// Зеркалит GET /admin/online (api/routes/admin.py).
export interface OnlineItem {
  subscription_id: number;
  label: string;
  tariff: Tariff;
  user_id: number;
  first_name: string;
  tg_username: string;
  node_id: number | null;
  node_location: string;
  node_name: string;
  country: string;
  city: string;
  client_ip: string;
  last_seen: string | null;
  auths_5min: number;
  end_at: string | null;
}

// Статистика посещённых сайтов подписки — GET /admin/subscriptions/{id}/domains.
// БЕЗ IP (по требованию): только домен + время + кол-во заходов.
export interface DomainVisitItem {
  domain: string;
  visits: number;
  first_seen: string | null;
  last_seen: string | null;
}

export interface DomainStatsResponse {
  summary: {
    unique_domains: number;
    total_visits: number;
    since: string | null;
    until: string | null;
    window_days: number;
  };
  items: DomainVisitItem[];
}

export interface AuditLogItem {
  id: number;
  admin_id: number;
  admin_username: string | null;
  action: string;
  target_user_id: number | null;
  payload: Record<string, unknown>;
  ip: string | null;
  user_agent: string | null;
  created_at: string | null;
}
